import { io, Socket } from 'socket.io-client';
import { useUserStore } from '@/stores/user';
import { useAgentStore } from '@/stores/agent';
import { useConnectionStore } from '@/stores/connection';
import { refreshToken } from '@/services/token';
import type { WSAgentEvent, WSQueueEvent, WSQueuedEvent, WSContactsDomainEvent, WSSubscription } from '@/types';

let socket: Socket | null = null;
let subscriptions: WSSubscription[] = [];

/**
 * Connect to the NetSapiens WebSocket server and set up event routing.
 */
export function connectWebSocket() {
  const userStore = useUserStore();
  const agentStore = useAgentStore();
  const connectionStore = useConnectionStore();

  if (socket) {
    socket.disconnect();
  }

  connectionStore.setWsStatus('connecting');

  socket = io(userStore.wssUrl, {
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 5000,
    reconnectionDelayMax: 30000,
    transports: ['websocket'],
    forceNew: true,
  });

  socket.on('connect', () => {
    connectionStore.setWsStatus('connected');
    createSubscriptions();
    reconcileState();
  });

  socket.on('disconnect', (reason) => {
    console.debug('[WS] Disconnected:', reason);
    connectionStore.setWsStatus('disconnected');
  });

  socket.on('connect_error', (err) => {
    console.debug('[WS] Connect error:', err.message);
    connectionStore.setWsStatus('disconnected');
  });

  socket.on('reconnect', () => {
    console.debug('[WS] Reconnected');
    connectionStore.setWsStatus('connected');
    refreshTokenAndResubscribe();
  });

  socket.on('subscription_error', (error) => {
    console.warn('[WS] Subscription error:', error);
    console.warn('[WS] Current JWT length:', userStore.jwt?.length);
    console.warn('[WS] Current domain:', userStore.domain);
    console.warn('[WS] Active subscriptions count:', subscriptions.length);
    refreshTokenAndResubscribe();
  });

  // ─── Event routing ─────────────────────────────────────────

  socket.on('agent', (data: WSAgentEvent) => {
    agentStore.handleAgentUpdate(data);
  });

  socket.on('agent-device', (data: WSAgentEvent) => {
    agentStore.handleAgentUpdate(data);
  });

  socket.on('queue', (data: WSQueueEvent) => {
    agentStore.handleQueueUpdate(data);
  });

  socket.on('queued', (data: WSQueuedEvent) => {
    agentStore.handleQueuedUpdate(data);
  });

  socket.on('contacts-domain', (data: WSContactsDomainEvent) => {
    console.debug('[WS] Received contacts-domain event:', data);
    agentStore.handlePortalStatus(data);
  });

  // Additional events can be added here as needed:
  // socket.on('voicemail', ...)
  // socket.on('chatavailable', ...)
  // socket.on('call', ...)
}

/**
 * Create all WebSocket subscriptions for the current user and queues.
 */
function createSubscriptions() {
  const userStore = useUserStore();
  const agentStore = useAgentStore();

  if (!socket) return;

  subscriptions = [
    { type: 'agent' },
    { type: 'queue' },
    { type: 'contacts-domain', user: userStore.user },
    { type: 'voicemail', user: userStore.user },
    { type: 'chat', user: userStore.user },
    { type: 'call', user: userStore.user },
  ];

  // Per-queue subscriptions
  for (const queue of agentStore.queues) {
    subscriptions.push({ type: 'queued', filter: queue.queue_name });
    subscriptions.push({ type: 'call', subtype: 'queue', filter: queue.queue_name });
  }

  for (const sub of subscriptions) {
    console.debug('[WS] Subscribing to:', sub.type, sub);
    socket.emit('subscribe', {
      bearer: userStore.jwt,
      application: 'webphone',
      domain: userStore.domain,
      ...sub,
    });
  }
}

/**
 * Refresh the JWT and re-create all subscriptions.
 */
async function refreshTokenAndResubscribe() {
  const newToken = await refreshToken();
  if (newToken) {
    createSubscriptions();
  }
}

/**
 * After reconnect, fetch fresh agent data for all queues to reconcile
 * any events missed while disconnected.
 */
async function reconcileState() {
  const agentStore = useAgentStore();
  // Re-fetch all queue data from v1 API
  await agentStore.initializeFromNs();
}

/**
 * Disconnect the WebSocket.
 */
export function disconnectWebSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  subscriptions = [];
}
